import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { inject, observer } from 'mobx-react';
import ProfileOneIcon from '../svg/ProfileOneIcon';
import OrdersIcon from '../svg/OrdersIcon';
import LogoutIcon from '../svg/LogoutIcon';
import BoxOneIcon from '../svg/BoxOneIcon';
import HeartIcon from '../svg/HeartIcon';
import UserMenuSkeleton from './UserMenuSkeleton';
import Link from '@/components/common/Link';
import { handleLogout } from '@/utils/auth';

function UserMenu({ color, generalSettingsStore }) {
  const [isOpen, setIsOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const menuRef = useRef(null);
  const router = useRouter();
  const t = useTranslations('Account');

  // Check if cart management is enabled
  const isCartManagementEnabled = generalSettingsStore?.showCartManageMultiple;

  // Check if favorites is enabled
  const isFavoritesEnabled = generalSettingsStore?.showFavorites;

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchUserData = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      router.push('/login');
      return;
    }

    try {
      const response = await fetch('/api/user', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUserData(data);
        localStorage.setItem('userData', JSON.stringify(data));
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        router.push('/login');
      }
    } catch (error) {
      console.error('An error occurred', error);
    }
  }, [router]);

  useEffect(() => {
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      setUserData(JSON.parse(storedUserData));
    } else {
      fetchUserData();
    }
  }, [fetchUserData]);

  const handleLogoutClick = async () => {
    setIsOpen(false);
    await handleLogout(router);
  };

  if (!userData) return <UserMenuSkeleton />;

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center hover:underline whitespace-nowrap"
      >
        <ProfileOneIcon width="24px" fill={color} className="inline me-1 h-4 w-4 xl:h-6 xl:w-6" />
        <span className="hidden md:inline">{t('hello')}, {userData.first_name}</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
          <Link
            href="/my-account"
            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            onClick={() => setIsOpen(false)}
          >
            <ProfileOneIcon width="20px" fill={color} className="me-2" />
            <span>{userData.first_name} {userData.last_name}</span>
          </Link>
          <Link
            href="/my-account?tab=my-orders"
            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            onClick={() => setIsOpen(false)}
          >
            <OrdersIcon width="20px" fill={color} className="me-2" />
            <span>{t('my_orders')}</span>
          </Link>

          {isCartManagementEnabled && (
            <Link
              href="/my-account?tab=manage-carts"
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => setIsOpen(false)}
            >
              <BoxOneIcon width="20px" height="20px" fill={color} className="me-2" />
              <span>{t('manage_carts')}</span>
            </Link>
          )}

          {isFavoritesEnabled && (
            <Link
              href="/my-account?tab=my-favorites"
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => setIsOpen(false)}
            >
              <HeartIcon width="20" height="20" fill={color} stroke={color} className="me-2" />
              <span>{t('my_favorites')}</span>
            </Link>
          )}

          <button
            onClick={handleLogoutClick}
            className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full mt-2"
            data-testid="logout-button"
          >
            <LogoutIcon width="20px" fill="currentColor" className="me-2" />
            <span>{t('logout')}</span>
          </button>
        </div>
      )}
    </div>
  );
}

UserMenu.propTypes = {
  color: PropTypes.string.isRequired,
  generalSettingsStore: PropTypes.object,
};

export default inject('generalSettingsStore')(observer(UserMenu));